import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import {
  ArrowUpRight,
  Briefcase,
  Building2,
  CalendarOff,
  Clock,
  GraduationCap,
  LayoutGrid,
  Layers,
  Loader2,
  Pencil,
  Percent,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { cn } from '@/lib/utils'
import { resourceProfilesApi, type ResourceProfile } from '@/api/resourceCenterApi'
import { useResourceProfile, useResources } from '@/hooks/useResourceCenter'
import { useResourceMethods } from '@/hooks/useResourceCapabilities'
import { WORK_TYPE_CHIPS, shiftSummary, splitLabel } from '@/lib/assignmentGroups'
import { todayIso } from '@/lib/resourceCalendar'
import { PersonGroupsDialog } from '../PersonGroupsDialog'
import { CapacityCalendar } from './CapacityCalendar'
import { Empty, ErrorLine, SectionCard, StatTile, dateRange, initials, longDate, useProfileWrite } from './shared'
import { SplitSection } from './SplitSection'
import { ShiftSection } from './ShiftSection'
import { AbsenceSection, SupportSection } from './TimeAwaySections'

const NO_SUPERVISOR = '__none__'

const TAB_VALUES = ['overview', 'shift', 'time-away', 'assignment', 'qualifications'] as const
type ProfileTab = (typeof TAB_VALUES)[number]

const TAB_META: { value: ProfileTab; label: string; icon: LucideIcon }[] = [
  { value: 'overview', label: 'Overview', icon: LayoutGrid },
  { value: 'shift', label: 'Shift', icon: Clock },
  { value: 'time-away', label: 'Time away', icon: CalendarOff },
  { value: 'assignment', label: 'Assignment', icon: Briefcase },
  { value: 'qualifications', label: 'Qualifications', icon: GraduationCap },
]

/**
 * Resource profile — the six sections (deck slide 9) on their own page
 * (/resources/people/:userId). Every change refetches the profile, the People
 * grid and the calendar, so they update at once.
 *
 * The header stays on top; everything else is tabbed (and the tab lives in
 * the URL, `?tab=`, so a link or a reload lands on it) — Overview for a
 * read-only glance, then one tab per thing a supervisor manages.
 */
export function ProfileView({ userId, canManage }: { userId: string; canManage: boolean }) {
  const profile = useResourceProfile(userId)
  const p = profile.data
  const [params, setParams] = useSearchParams()
  const tab: ProfileTab = TAB_VALUES.includes(params.get('tab') as ProfileTab) ? (params.get('tab') as ProfileTab) : 'overview'
  const setTab = (t: string) =>
    setParams(
      prev => {
        const next = new URLSearchParams(prev)
        if (t === 'overview') next.delete('tab')
        else next.set('tab', t)
        return next
      },
      { replace: true },
    )

  if (profile.error) return <p className='rounded-xl bg-destructive/10 p-4 text-sm text-destructive'>{profile.error.message}</p>
  if (!p)
    return (
      <div className='flex items-center gap-2 p-10 text-sm text-muted-foreground'>
        <Loader2 className='h-4 w-4 animate-spin' /> Loading profile…
      </div>
    )
  return (
    <div className='space-y-5'>
      <ProfileHeader profile={p} canManage={canManage} />
      <Tabs value={tab} onValueChange={setTab} className='space-y-4'>
        <TabsList className='h-auto w-full justify-start gap-1 rounded-none border-b bg-transparent p-0'>
          {TAB_META.map(({ value, label, icon: Icon }) => (
            <TabsTrigger
              key={value}
              value={value}
              className='-mb-px gap-2 rounded-none border-b-2 border-transparent bg-transparent px-4 py-2.5 text-sm text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'
            >
              <Icon className='h-4 w-4' /> {label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value='overview' className='mt-0 space-y-5'>
          <SummaryStrip profile={p} />
          <CapacityCalendar key={`cap-${p.userId}`} profile={p} canManage={canManage} />
          <UpcomingAbsences profile={p} />
        </TabsContent>

        <TabsContent value='shift' className='mt-0'>
          <ShiftSection key={`shift-${p.shift?.id ?? 'none'}`} profile={p} canManage={canManage} />
        </TabsContent>

        <TabsContent value='time-away' className='mt-0'>
          <div className='grid gap-5 md:grid-cols-2'>
            <SupportSection profile={p} canManage={canManage} />
            <AbsenceSection profile={p} canManage={canManage} />
          </div>
        </TabsContent>

        <TabsContent value='assignment' className='mt-0 space-y-5'>
          <GroupsSection profile={p} canManage={canManage} />
          <SplitSection key={`split-${p.split?.id ?? 'none'}`} profile={p} canManage={canManage} />
        </TabsContent>

        <TabsContent value='qualifications' className='mt-0'>
          <MethodsSection userId={p.userId} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

/** Five facts a supervisor needs before reading further: team, primary group,
 * shift, split and what's left of today. */
function SummaryStrip({ profile }: { profile: ResourceProfile }) {
  const today = todayIso()
  const primaryGroup = profile.groups.find(g => g.isPrimary)
  const shift = shiftSummary(profile.shift)
  const split = splitLabel(profile.split)
  const upcoming = profile.unavailabilities.filter(u => !u.toDate || u.toDate >= today).length

  return (
    <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5'>
      <StatTile icon={Building2} label='Teams' value={profile.teams.length ? profile.teams.map(t => t.name).join(', ') : '—'} />
      <StatTile icon={Layers} label='Primary group' value={primaryGroup?.name ?? '—'} />
      <StatTile icon={Clock} label='Current shift' value={shift?.title ?? 'Not set'} sub={shift?.detail} />
      <StatTile icon={Percent} label='Split' value={split ?? 'Not set'} />
      <StatTile
        icon={CalendarOff}
        label='Upcoming absences'
        value={upcoming}
        sub={upcoming === 0 ? 'None planned' : undefined}
      />
    </div>
  )
}

/** Overview, read-only glance at what Time away otherwise manages. */
function UpcomingAbsences({ profile }: { profile: ResourceProfile }) {
  const today = todayIso()
  const upcoming = [...profile.unavailabilities]
    .filter(u => !u.toDate || u.toDate >= today)
    .sort((a, b) => a.fromDate.localeCompare(b.fromDate))
    .slice(0, 5)

  return (
    <SectionCard icon={CalendarOff} title='Upcoming absences' hint='Read-only — manage and approve on the Time away tab'>
      {upcoming.length === 0 ? (
        <Empty>No upcoming absences.</Empty>
      ) : (
        <ul className='space-y-1.5'>
          {upcoming.map(u => (
            <li key={u.id} className='flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs'>
              <span className='rounded bg-rose-50 px-1.5 py-0.5 font-semibold text-rose-700'>{u.type.code}</span>
              <span className='min-w-0 flex-1 truncate'>
                {u.type.name} <span className='text-muted-foreground'>· {dateRange(u.fromDate, u.toDate)}</span>
              </span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 text-[11px] font-medium',
                  u.status === 'APPROVED' ? 'bg-emerald-50 text-emerald-700' : 'bg-muted text-muted-foreground',
                )}
              >
                {u.status === 'APPROVED' ? 'Approved' : 'Planned'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  )
}

/** Name, supervisor and lab role (slides 4 and 6: "Supervisor", "System & Lab Role"). */
function ProfileHeader({ profile, canManage }: { profile: ResourceProfile; canManage: boolean }) {
  const resources = useResources()
  const write = useProfileWrite()
  const supervisors = (resources.data ?? []).filter(r => r.isSupervisor && r.userId !== profile.userId)
  // Keep the current supervisor selectable even if they are no longer flagged.
  const options =
    profile.supervisor && !supervisors.some(s => s.userId === profile.supervisor!.id)
      ? [{ userId: profile.supervisor.id, name: profile.supervisor.name }, ...supervisors]
      : supervisors

  return (
    <div className='flex flex-wrap items-center gap-5 rounded-2xl border bg-background p-5 shadow-sm'>
      <div className='flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary to-sky-500 text-xl font-semibold text-white shadow-sm'>
        {initials(profile.name)}
      </div>
      <div className='min-w-0 flex-1'>
        <h1 className='truncate text-2xl font-semibold tracking-tight'>{profile.name}</h1>
        <p className='truncate text-sm text-muted-foreground'>
          {profile.email}
          {profile.teams.length > 0 && (
            <>
              {' '}· {profile.teams.length === 1 ? 'Team' : 'Teams'}{' '}
              <span className='font-medium text-foreground'>{profile.teams.map(t => t.name).join(', ')}</span>
            </>
          )}
        </p>
      </div>

      <div className='flex flex-wrap items-end gap-3'>
        <label className='space-y-1.5'>
          <span className='block text-xs font-medium text-muted-foreground'>Supervisor</span>
          <Select
            value={profile.supervisor?.id ?? NO_SUPERVISOR}
            disabled={!canManage || write.pending}
            onValueChange={v =>
              write.run(() =>
                resourceProfilesApi.setSupervision(profile.userId, { supervisorId: v === NO_SUPERVISOR ? null : v }),
              )
            }
          >
            <SelectTrigger className='h-10 w-56 text-sm' aria-label='Supervisor'>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_SUPERVISOR}>No supervisor</SelectItem>
              {options.map(s => (
                <SelectItem key={s.userId} value={s.userId}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <div className='space-y-1.5'>
          <span className='block text-xs font-medium text-muted-foreground'>Lab role</span>
          <label className='flex h-10 w-56 items-center gap-2 rounded-md border bg-background px-3'>
            <ShieldCheck className='h-4 w-4 shrink-0 text-muted-foreground' />
            <span className='flex-1 text-sm font-medium'>{profile.isSupervisor ? 'Supervisor' : 'Tech'}</span>
            <Switch
              checked={profile.isSupervisor}
              disabled={!canManage || write.pending}
              aria-label='Supervisor lab role'
              onCheckedChange={v => write.run(() => resourceProfilesApi.setSupervision(profile.userId, { isSupervisor: v }))}
            />
          </label>
        </div>
      </div>
      {write.error && (
        <div className='w-full'>
          <ErrorLine message={write.error} />
        </div>
      )}
    </div>
  )
}

/** 1 · Groups (slide 3): one Primary and the rest Secondary; their teams follow from the groups. */
function GroupsSection({ profile, canManage }: { profile: ResourceProfile; canManage: boolean }) {
  const [editing, setEditing] = useState(false)
  const types = profile.split?.workTypes ?? []
  return (
    <SectionCard
      icon={Layers}
      title='Groups'
      hint='What work can be assigned'
      action={
        canManage && (
          <Button size='sm' variant='ghost' className='h-7 text-xs' onClick={() => setEditing(true)}>
            <Pencil className='mr-1 h-3 w-3' /> Edit
          </Button>
        )
      }
    >
      {profile.groups.length === 0 ? (
        <Empty>Not in any group yet.</Empty>
      ) : (
        <div className='flex flex-wrap gap-1.5'>
          {[...profile.groups]
            .sort((a, b) => Number(b.isPrimary) - Number(a.isPrimary))
            .map(g => (
              <span
                key={g.id}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium',
                  g.isPrimary && 'border-primary/40 bg-primary/5 text-primary',
                )}
              >
                {g.name}
                <span className='text-[10px] uppercase tracking-wide opacity-70'>{g.isPrimary ? 'Primary' : 'Secondary'}</span>
              </span>
            ))}
        </div>
      )}
      <div>
        <p className='mb-1.5 text-xs text-muted-foreground'>Work they may be given</p>
        <div className='flex gap-1'>
          {types.length === 0 && <span className='text-xs text-muted-foreground'>Set a split to choose</span>}
          {types.map(t => (
            <span key={t} title={WORK_TYPE_CHIPS[t].title} className={cn('rounded-full px-2 py-0.5 text-xs font-semibold', WORK_TYPE_CHIPS[t].className)}>
              {WORK_TYPE_CHIPS[t].label}
            </span>
          ))}
        </div>
      </div>
      {editing && <PersonGroupsDialog person={profile} canManage={canManage} onOpenChange={o => !o && setEditing(false)} />}
    </SectionCard>
  )
}

/** 2 · Methods (slide 4): what they may run (PR) or review (RV), with expiry. */
function MethodsSection({ userId }: { userId: string }) {
  const { list } = useResourceMethods()
  const rows = (list.data ?? []).filter(m => m.userId === userId).sort((a, b) => a.preference - b.preference)
  return (
    <SectionCard
      icon={GraduationCap}
      title='Methods'
      hint='Qualifications'
      action={
        <Link
          to='/configure?tab=method&view=qualifications'
          className='inline-flex items-center gap-0.5 text-xs font-medium text-primary hover:underline'
        >
          Qualifications <ArrowUpRight className='h-3 w-3' />
        </Link>
      }
    >
      {list.error && <p className='text-xs text-destructive'>{list.error.message}</p>}
      {list.isSuccess && rows.length === 0 ? (
        <Empty>No methods yet — add them under Qualifications.</Empty>
      ) : (
        <ul className='space-y-1.5'>
          {rows.map(m => (
            <li key={m.id} className={cn('flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-xs', m.expired && 'opacity-60')}>
              <span className='min-w-0 flex-1 truncate font-medium'>{m.methodName}</span>
              {(m.canProcess ?? true) && <span className={cn('rounded-full px-1.5 py-0.5 font-semibold', WORK_TYPE_CHIPS.PROCESS.className)}>PR</span>}
              {(m.canReview ?? true) && <span className={cn('rounded-full px-1.5 py-0.5 font-semibold', WORK_TYPE_CHIPS.REVIEW.className)}>RV</span>}
              <span className={cn('w-24 text-right', m.expired ? 'text-destructive' : 'text-muted-foreground')}>
                {m.qualifiedUntil ? `${m.expired ? 'Expired' : 'Until'} ${longDate(m.qualifiedUntil)}` : 'No expiry'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </SectionCard>
  )
}
